// st-auth-client: single owner of auth state, CSRF tokens, and auth status events.
//
// Key concepts:
// - Physical session state: unknown → loggedIn / loggedOut. Login workflow state
//   remains in the deterministic app core rather than being duplicated here.
// - CSRF epoch invalidation: instead of invalidating cache entries (which races
//   with in-flight fetches), we bump a monotonic epoch counter so stale fetches
//   write to keys that are never read again.
// - Command serialization: all auth commands (login, logout, startup session probe, account
//   updates) run through a single-worker queue to prevent interleaved state
//   transitions.
// - Response recovery: invalid_csrf invalidates CSRF; 401 probes the authoritative
//   session before emitting any auth boundary event.
//
// Ownership boundary: callers never touch CSRF or auth state directly. They
// use `getHeaders()` for request headers and the `statuses` Stream to observe
// auth state changes.

import { HttpClient, HttpClientError, HttpClientRequest } from 'effect/unstable/http'
import {
  HttpStatusError,
  InvalidRequestError,
  InvalidResponseError,
  inferFetchFailure,
  OAuthProvider,
  SendPasswordResetEmailResponse,
  TimeoutError,
  TransportError,
  isHttpStatusError
} from '@st/sdk'
import type { CommandError, SDKResponseFor, STSDK } from '@st/sdk'
import {
  Cache,
  Deferred,
  Duration,
  Effect,
  Exit,
  Fiber,
  Queue,
  Ref,
  Schedule,
  Stream,
  SubscriptionRef
} from 'effect'
import {
  AuthError,
  AuthSignUpOpts,
  AuthStatusEvent,
  LoginWithEmailPasswordResult,
  LoginWithMFACodeResult,
  LoginWithOAuthCodeResult,
  User
} from './auth-client-adapter'
import type { AuthFailure } from './auth-client-adapter'
import { ErrorReporting, type ErrorReportingService } from '~/effects/error-reporting'
import { reportHandledError } from '../error-reporting/report-handled-error'
import { match } from '@st/pattern'

export type AuthTransportError = TransportError | TimeoutError | HttpStatusError

type SessionState =
  | { status: 'unknown' }
  | { status: 'loggedOut' }
  | { status: 'loggedIn'; user: User }

export interface AuthClient {
  // Logs out the current user. Best-effort: transitions to loggedOut even if server call fails.
  logout: () => Effect.Effect<void>
  // Replays the current terminal auth state and observes later status changes.
  statuses: Stream.Stream<AuthStatusEvent>
  // Creates a new account and logs in on success.
  signUp: (opts: AuthSignUpOpts) => Effect.Effect<User, AuthFailure>
  // Authenticates with email/password. May return `mfaRequired` if MFA is enabled.
  loginWithEmailPassword: (
    email: string,
    password: string
  ) => Effect.Effect<LoginWithEmailPasswordResult>
  // Completes MFA challenge for an in-progress email/password login.
  completeLoginWithMFACode: (
    sessionId: string,
    code: string
  ) => Effect.Effect<LoginWithMFACodeResult>
  // Authenticates via a one-time magic link token.
  loginWithMagicLink: (magicLinkToken: string) => Effect.Effect<User, AuthFailure>
  // Completes OAuth callback with provider code and state.
  loginWithOAuthCode: (
    provider: OAuthProvider,
    code: string,
    state: string
  ) => Effect.Effect<LoginWithOAuthCodeResult>
  // Resets password using a reset token. Returns Result-style {ok, value/error}.
  resetPassword: (
    token: string,
    password: string,
    passwordConfirmation: string
  ) => Effect.Effect<
    { ok: true; value: { user: User | undefined } } | { ok: false; error: AuthError },
    never
  >
  // Sends a password reset email to the given address.
  sendPasswordResetEmail: (
    email: string,
    continueUrl: string
  ) => Effect.Effect<SendPasswordResetEmailResponse, AuthFailure>
  // Updates the user's display name.
  setProfileName: (userId: string, profileName: string) => Effect.Effect<void, AuthFailure>
  // Sends a verification code to the user's phone.
  sendPhoneNumberVerificationCode: () => Effect.Effect<void, AuthFailure>
  // Updates the user's phone number (requires current password + optional verification code).
  updatePhoneNumber: (args: {
    phoneNumber: string
    currentPassword: string
    code?: string
  }) => Effect.Effect<SDKResponseFor<'accounts/updatePhoneNumber'>, AuthFailure>
  // Verifies the user's phone number with a code.
  verifyPhoneNumber: (
    code: string
  ) => Effect.Effect<SDKResponseFor<'accounts/verifyPhoneNumber'>, AuthFailure>
  // Enables MFA for the given method.
  enableMultiFactorAuthentication: (method: 'phone_number') => Effect.Effect<void, AuthFailure>
  // Returns the current CSRF header for SDK requests, including anonymous auth commands.
  getHeaders: () => Effect.Effect<Record<string, string>, unknown, never>
  // Boundary events emitted by the application HTTP client.
  sessionRejected: () => Effect.Effect<void, never, never>
  csrfRejected: () => Effect.Effect<void, never, never>
}

export type LiveAuthClient = AuthClient & {
  // Owned lifecycle hook used only by the scoped Auth layer and direct client tests.
  shutdown: () => Effect.Effect<void, never, never>
}

type WithAuthHttpClientOpts = {
  httpClient: HttpClient.HttpClient
  authClient: Pick<AuthClient, 'getHeaders' | 'sessionRejected' | 'csrfRejected'>
}

export function withAuthHttpClient({
  httpClient,
  authClient
}: WithAuthHttpClientOpts): HttpClient.HttpClient {
  // HTTP strategy:
  // 1) resolve auth headers for every outgoing request right before dispatch
  // 2) delegate 401 session reconciliation and invalid_csrf invalidation to the auth client
  //
  // The middleware intentionally does not retry by itself. Retry policy lives in
  // callers that understand command semantics (authSendRecoverable below).
  return httpClient.pipe(
    HttpClient.mapRequestEffect((request) =>
      Effect.gen(function* () {
        const authHeaders = yield* authClient.getHeaders()
        return HttpClientRequest.setHeaders(request, authHeaders)
      }).pipe(
        Effect.mapError(
          (error) =>
            new HttpClientError.HttpClientError({
              reason: new HttpClientError.TransportError({
                request,
                cause: error,
                description: 'failed to resolve auth headers'
              })
            })
        )
      )
    ),
    HttpClient.transformResponse((effect) =>
      effect.pipe(
        Effect.tap((response) => {
          if (response.status == 401) {
            return authClient.sessionRejected()
          }
          if (response.status != 403) {
            return Effect.void
          }
          return response.json.pipe(
            Effect.flatMap((body) => {
              if (
                body &&
                typeof body == 'object' &&
                'error' in body &&
                body.error == 'invalid_csrf'
              ) {
                return authClient.csrfRejected()
              }
              return Effect.void
            })
          )
        })
      )
    )
  )
}

export type MakeAuthClientOpts = {
  oauthRedirectBaseUrl: string
  apiBaseUrl: string
  httpClient: HttpClient.HttpClient
  makeRequestId: Effect.Effect<string>
  clearLocalGrantsCookie: Effect.Effect<void>
  createSdk: (
    getRequestHeaders: () => Effect.Effect<Record<string, string>, unknown, never>
  ) => STSDK
  errorReporting: ErrorReportingService
}

export function makeAuthClient({
  oauthRedirectBaseUrl,
  apiBaseUrl,
  httpClient,
  makeRequestId,
  clearLocalGrantsCookie,
  createSdk,
  errorReporting
}: MakeAuthClientOpts): Effect.Effect<LiveAuthClient, never, never> {
  // Auth client ownership model:
  // - this module is the single owner of auth state transitions and auth status events
  // - CSRF token lifecycle is also owned here so callers only ask for headers
  // - all auth commands are serialized through one queue to preserve deterministic ordering
  //
  // CSRF invariant:
  // - tokens are read from cache keys scoped by `csrfEpochRef`
  // - invalidation bumps epoch instead of mutating in-flight cache lookups
  // - stale in-flight fetches can only populate old keys, never the active key
  return Effect.gen(function* () {
    type LoginResponse = { user: User; error?: AuthError }
    interface EmailPasswordLoginResponse extends LoginResponse {
      mfaSession?: unknown
    }
    interface OAuthLoginResponse extends LoginResponse {
      postLoginRedirectUrl?: string
    }

    const dieOnAuthContractDefect = <A, R>(
      effect: Effect.Effect<A, CommandError, R>
    ): Effect.Effect<A, AuthTransportError, R> =>
      effect.pipe(
        Effect.catchTags({
          InvalidRequestError: (error) => Effect.die(error),
          InvalidResponseError: (error) => Effect.die(error)
        })
      )

    // Physical session state machine:
    //   unknown ──(startup session probe)──→ loggedIn | loggedOut
    //   loggedOut ──(successful login)──→ loggedIn
    //   loggedIn ──(logout)──→ loggedOut
    //
    // Login progress, MFA, and errors belong to the app workflow state. A pending or failed
    // login never erases the last server-observed session here.
    const authStateRef = yield* SubscriptionRef.make<SessionState>({ status: 'unknown' })
    const authCommandQueue = yield* Queue.unbounded<Effect.Effect<void, never, never>>()

    // Performs the raw `/csrf` HTTP call and normalizes all transport/status parsing
    // into domain-friendly failures for the rest of the auth client.
    const fetchCsrfToken = (requestId: string): Effect.Effect<string, CommandError> => {
      const csrfUrl = apiBaseUrl + '/csrf'
      const requestHeaders = { 'x-request-id': requestId }
      const requestTrace = { url: csrfUrl, method: 'GET', headers: requestHeaders }

      const request = HttpClientRequest.setHeaders(HttpClientRequest.get(csrfUrl), requestHeaders)

      return httpClient.execute(request).pipe(
        Effect.mapError((error) =>
          toHttpClientTransportError(error, {
            url: csrfUrl,
            method: 'GET',
            headers: requestHeaders
          })
        ),
        Effect.flatMap(
          (response): Effect.Effect<string, HttpStatusError | InvalidResponseError> => {
            if (response.status != 200) {
              return response.text.pipe(
                Effect.mapError(
                  (readError) =>
                    new HttpStatusError(
                      requestTrace,
                      { status: response.status, headers: response.headers },
                      { cause: readError, description: 'failed to read csrf error body' }
                    )
                ),
                Effect.flatMap((body) =>
                  Effect.fail(
                    new HttpStatusError(requestTrace, {
                      status: response.status,
                      headers: response.headers,
                      body
                    })
                  )
                )
              )
            }

            return response.json.pipe(
              Effect.mapError(
                (error) =>
                  new InvalidResponseError(requestTrace, 'invalid_json', {
                    cause: error,
                    response: { status: response.status, headers: response.headers }
                  })
              ),
              Effect.flatMap((json) => {
                let jsonObj: Record<string, unknown> | undefined
                if (json && typeof json == 'object') {
                  jsonObj = json as Record<string, unknown>
                }
                const token = jsonObj?.csrfToken ?? jsonObj?.csrf_token
                if (!token || typeof token != 'string') {
                  return Effect.fail(
                    new InvalidResponseError(requestTrace, 'schema_mismatch', {
                      response: { status: response.status, headers: response.headers },
                      description: 'CSRF response missing csrf_token'
                    })
                  )
                }

                return Effect.succeed(token)
              })
            )
          }
        )
      )
    }

    // CSRF token cache with epoch-scoped keys.
    //
    // Problem: Effect's Cache.invalidate does not cancel in-flight lookups. If a CSRF fetch
    // is in-flight when invalidation happens (e.g., during login), the fetch completes and
    // re-inserts the stale token — making invalidation a no-op. This caused production 403s
    // on post-login requests like markAsOpened.
    //
    // Solution: instead of invalidating cache entries, we change the lookup key. Each epoch
    // bump creates a new cache key (`csrf-token:0`, `csrf-token:1`, ...). Old in-flight
    // fetches write to old keys that are never read again. Fresh callers always read the
    // current epoch's key.
    const csrfTokenCache = yield* Cache.makeWith(
      (_cacheKey: string) => makeRequestId.pipe(Effect.flatMap(fetchCsrfToken)),
      {
        capacity: 1,
        timeToLive: (exit) => (Exit.isSuccess(exit) ? Duration.infinity : Duration.zero)
      }
    )
    // Monotonic generation counter for CSRF validity.
    // Bumping this ref invalidates the active CSRF key without racing in-flight lookups.
    const csrfEpochRef = yield* Ref.make(0)

    // Internal state write helper so all auth state transitions flow through one function.
    const setAuthState = (state: SessionState) => SubscriptionRef.set(authStateRef, state)
    // CSRF invalidation primitive.
    // We bump epoch instead of calling `Cache.invalidate` to avoid stale in-flight reinsertions.
    const clearCsrfToken = () => Ref.update(csrfEpochRef, (epoch) => epoch + 1)
    // Serializes auth commands through a single-worker queue.
    //
    // Why serialization matters: auth commands modify shared state (auth status, CSRF epoch,
    // session cookies). Without serialization, login and the startup probe could interleave
    // state transitions and produce inconsistent auth state. The queue ensures commands
    // execute one at a time in FIFO order.
    //
    // Implementation: Queue.unbounded + Deferred for result plumbing + a daemon fiber that
    // drains the queue forever. Callers block on the Deferred until their command completes.
    // The daemon fiber decouples caller interruption from command execution — if a caller
    // is interrupted (e.g., user navigates away), the in-flight command still completes.
    const enqueueAuthCommand = <A, E>(
      effect: Effect.Effect<A, E, never>,
      onQueued: Effect.Effect<void> = Effect.void
    ): Effect.Effect<A, E, never> =>
      Effect.gen(function* () {
        const result = yield* Deferred.make<Exit.Exit<A, E>>()
        const command = effect.pipe(
          Effect.exit,
          Effect.flatMap((exit) => Deferred.succeed(result, exit)),
          Effect.as(undefined)
        )
        yield* Queue.offer(authCommandQueue, command)
        yield* onQueued
        const exit = yield* Deferred.await(result)
        return yield* Effect.matchCauseEffectEager(exit, {
          onFailure: (cause) => Effect.failCause(cause),
          onSuccess: Effect.succeed
        })
      })

    // Reads the active epoch and resolves that epoch's CSRF cache entry.
    // Key point: after invalidation, callers read a brand new cache key.
    const getCsrfToken = (): Effect.Effect<string, CommandError> =>
      Ref.get(csrfEpochRef).pipe(
        Effect.map((epoch) => `csrf-token:${epoch}`),
        Effect.flatMap((cacheKey) => Cache.get(csrfTokenCache, cacheKey))
      )

    const getHeaders = () =>
      getCsrfToken().pipe(
        dieOnAuthContractDefect,
        Effect.map((token) => ({ 'x-csrf-token': token }))
      )

    // All SDK calls in this module are built on top of the same auth-aware header provider.
    const sdk = createSdk(getHeaders)

    // Centralized handled-error reporting for auth flows.
    // We report once with operation metadata and keep control flow in the caller.
    const reportAuthHandledError = (operation: string, error: unknown) =>
      reportHandledError(error, {
        source: 'auth-client',
        operation
      }).pipe(Effect.provideService(ErrorReporting, errorReporting))

    // Converts internal auth state into replayable public auth events.
    //
    // "Replayable" means: when a status stream starts, it immediately emits the current
    // physical session if known. Unknown waits for the first server-observed session.
    const toReplayableStatusEvent = (state: SessionState): AuthStatusEvent | null => {
      if (state.status == 'loggedIn') {
        return { type: 'loggedIn', user: state.user }
      }
      if (state.status == 'loggedOut') {
        return { type: 'loggedOut' }
      }
      return null
    }

    // Invalidates CSRF generation before committing logged-in state. SubscriptionRef
    // publishes the state write immediately, so ordering here prevents observers from
    // issuing their first authenticated request against the previous CSRF epoch.
    const loggedInEffect = (user: User) =>
      clearCsrfToken().pipe(Effect.andThen(setAuthState({ status: 'loggedIn', user })))

    // Account mutations already know their successful result. Keep the canonical
    // user current here instead of asking feature modules to refetch auth state.
    const updateLoggedInUserEffect = (update: (user: User) => User) =>
      SubscriptionRef.get(authStateRef).pipe(
        Effect.flatMap((state) => {
          if (state.status != 'loggedIn') {
            return Effect.void
          }

          const nextState: SessionState = { ...state, user: update(state.user) }
          return setAuthState(nextState)
        })
      )

    // Clears local grants and CSRF before publishing the logged-out state.
    const loggedOutEffect = () =>
      clearLocalGrantsCookie.pipe(
        Effect.andThen(clearCsrfToken()),
        Effect.andThen(setAuthState({ status: 'loggedOut' }))
      )

    // Auth commands use the raw HTTP client, so they handle invalid_csrf here and retry once.
    const authSendRecoverable = <A>(
      effect: Effect.Effect<A, CommandError>
    ): Effect.Effect<A, AuthTransportError, never> => {
      return effect.pipe(
        Effect.catchIf(isInvalidCsrfError, () => clearCsrfToken().pipe(Effect.andThen(effect))),
        dieOnAuthContractDefect
      )
    }

    // Shared wrapper for auth commands:
    // - transport-level 403 recovery
    // - operation-tagged handled-error reporting
    // - error normalization to user-facing AuthError
    const runHandledAuthCommand = <A>(
      opts: {
        operation: string
        unknownErrorMessage: string
      },
      command: Effect.Effect<A, CommandError>
    ): Effect.Effect<A, AuthFailure> => {
      return authSendRecoverable(command).pipe(
        Effect.tapError((error) => reportAuthHandledError(opts.operation, error)),
        Effect.mapError(() => toAuthFailure(opts.unknownErrorMessage))
      )
    }

    // Initial session probe. It only resolves `unknown`; another queued auth
    // command that already established state remains authoritative.
    const startupSessionProbe = () => {
      return Effect.gen(function* () {
        const session = yield* authSendRecoverable(
          sdk.send({
            type: 'accounts/getCurrentSession'
          })
        )
        const currentState = yield* SubscriptionRef.get(authStateRef)

        yield* match(session.status)
          .with('logged_in', () => {
            const user = session.user as User
            if (currentState.status == 'loggedIn' && currentState.user.id == user.id) {
              return Effect.void
            }
            // The probe restored an existing session rather than starting one: the
            // server renews the session (and its CSRF token) only on sign-in. The token
            // this probe was sent with still matches, so keeping it saves a round trip
            // before every first authenticated request.
            return setAuthState({ status: 'loggedIn', user })
          })
          .with('logged_out', () => {
            if (currentState.status != 'unknown') {
              return Effect.void
            }
            return loggedOutEffect()
          })
          .exhaustive()
      })
    }

    // A 401 describes the session used by that request. Re-read the current shared cookie
    // before changing app state because another tab or a later login may already have replaced it.
    // The auth FIFO gives that probe one deterministic position among session mutations.
    const requestSessionReconciliation = () =>
      enqueueAuthCommand(
        Effect.gen(function* () {
          const currentState = yield* SubscriptionRef.get(authStateRef)
          if (currentState.status != 'loggedIn') {
            return
          }

          const session = yield* authSendRecoverable(
            sdk.send({
              type: 'accounts/getCurrentSession'
            })
          )

          yield* match(session.status)
            .with('logged_in', () => {
              const user = session.user as User
              if (currentState.user.id == user.id) {
                return Effect.void
              }
              return loggedInEffect(user)
            })
            .with('logged_out', () => loggedOutEffect())
            .exhaustive()
        })
      ).pipe(
        Effect.tapError((error) => reportAuthHandledError('sessionReconciliation', error)),
        Effect.catchTags({
          HttpStatusError: (error) => {
            if (isTransientHttpStatus(error.response.status)) {
              return Effect.void
            }
            return Effect.die(error)
          },
          TimeoutError: () => Effect.void,
          TransportError: () => Effect.void
        })
      )

    const startupRetrySchedule = Schedule.exponential('100 millis').pipe(
      Schedule.while(
        ({ input: error }: { input: AuthTransportError }) =>
          error._tag == 'TransportError' ||
          error._tag == 'TimeoutError' ||
          (error._tag == 'HttpStatusError' && isTransientHttpStatus(error.response.status))
      )
    )

    // Dedicated daemon worker that drains serialized auth commands forever.
    // Queue ownership remains inside this module so callers cannot bypass ordering.
    // Both owned fibers are made interruptible explicitly because layer and resource
    // acquisition can otherwise pass an uninterruptible runtime flag into a fork.
    const authCommandWorker = yield* Effect.forever(
      Queue.take(authCommandQueue).pipe(Effect.flatMap((command) => command))
    ).pipe(Effect.interruptible, Effect.forkDetach)

    // Queue the initial session probe before exposing the client. Terminal auth
    // state lives in SubscriptionRef, so no observation can miss its result and
    // startup no longer depends on a callback subscriber existing first.
    const startupQueued = yield* Deferred.make<void>()
    const startupRefresh = yield* enqueueAuthCommand(
      startupSessionProbe(),
      Deferred.succeed(startupQueued, undefined).pipe(Effect.asVoid)
    ).pipe(
      Effect.tapError((error) => reportAuthHandledError('startupRefresh', error)),
      Effect.retry(startupRetrySchedule),
      Effect.interruptible,
      Effect.forkDetach
    )
    yield* Deferred.await(startupQueued)

    // SubscriptionRef atomically replays the current terminal state and then
    // streams later transitions. No parallel notification mechanism exists.
    const statuses = SubscriptionRef.changes(authStateRef).pipe(
      Stream.map(toReplayableStatusEvent),
      Stream.filter((event): event is AuthStatusEvent => event != null)
    )

    // Registration flow:
    // run in queue, normalize transport errors, then commit logged-in state on success.
    const signUp: (opts: AuthSignUpOpts) => Effect.Effect<User, AuthFailure> = Effect.fn(
      'Auth.signUp'
    )(({ email, name, password }: AuthSignUpOpts): Effect.Effect<User, AuthFailure> => {
      return enqueueAuthCommand(
        runHandledAuthCommand<LoginResponse>(
          {
            operation: 'signUp',
            unknownErrorMessage: 'Failed to sign up'
          },
          sdk.send({
            type: 'accounts/registerAccount',
            name,
            email,
            password
          })
        ).pipe(
          Effect.flatMap(raiseAuthErrorEffect),
          Effect.map((res) => res.user),
          Effect.tap((user) => loggedInEffect(user))
        )
      )
    })

    // Email/password login flow with explicit MFA branching and loginFailed event emission.
    const loginWithEmailPassword: (
      email: string,
      password: string
    ) => Effect.Effect<LoginWithEmailPasswordResult> = Effect.fn('Auth.loginWithEmailPassword')((
      email: string,
      password: string
    ): Effect.Effect<LoginWithEmailPasswordResult> => {
      return enqueueAuthCommand(
        runHandledAuthCommand<EmailPasswordLoginResponse>(
          {
            operation: 'loginWithEmailPassword',
            unknownErrorMessage: 'Failed to log in'
          },
          sdk.send({
            type: 'accounts/loginWithEmailPassword',
            email,
            password
          })
        ).pipe(
          Effect.matchEffect({
            onFailure: (error) =>
              Effect.succeed({ status: 'failed', error } satisfies LoginWithEmailPasswordResult),
            onSuccess: (res): Effect.Effect<LoginWithEmailPasswordResult> => {
              if (res.mfaSession) {
                return Effect.succeed({
                  status: 'mfaRequired',
                  session: res.mfaSession
                })
              }

              if (res.error) {
                return Effect.succeed({ status: 'failed', error: res.error })
              }

              return loggedInEffect(res.user).pipe(
                Effect.as({ status: 'succeeded', user: res.user })
              )
            }
          })
        )
      )
    })

    // Completes MFA login challenge and commits logged-in state on success.
    const completeLoginWithMFACode: (
      sessionId: string,
      code: string
    ) => Effect.Effect<LoginWithMFACodeResult> = Effect.fn('Auth.completeLoginWithMFACode')((
      sessionId: string,
      code: string
    ): Effect.Effect<LoginWithMFACodeResult> => {
      return enqueueAuthCommand(
        runHandledAuthCommand<LoginResponse>(
          {
            operation: 'completeLoginWithMFACode',
            unknownErrorMessage: 'Failed to log in'
          },
          sdk.send({
            type: 'accounts/completeLoginWithMultiFactorCode',
            mfaSessionId: sessionId,
            code
          })
        ).pipe(
          Effect.matchEffect({
            onFailure: (error) =>
              Effect.succeed({ status: 'failed', error } satisfies LoginWithMFACodeResult),
            onSuccess: (res): Effect.Effect<LoginWithMFACodeResult> => {
              if (res.error) {
                return Effect.succeed({ status: 'failed', error: res.error })
              }

              return loggedInEffect(res.user).pipe(
                Effect.as({ status: 'succeeded', user: res.user })
              )
            }
          })
        )
      )
    })

    // Magic-link login path with same queue + handled-error guarantees as other login methods.
    const loginWithMagicLink: (magicLinkToken: string) => Effect.Effect<User, AuthFailure> =
      Effect.fn('Auth.loginWithMagicLink')((
        magicLinkToken: string
      ): Effect.Effect<User, AuthFailure> => {
        return enqueueAuthCommand(
          runHandledAuthCommand<LoginResponse>(
            {
              operation: 'loginWithMagicLink',
              unknownErrorMessage: 'Failed to log in'
            },
            sdk.send({
              type: 'accounts/loginWithMagicLink',
              magicLinkToken: magicLinkToken
            })
          ).pipe(
            Effect.flatMap(raiseAuthErrorEffect),
            Effect.map((res) => res.user),
            Effect.tap((user) => loggedInEffect(user))
          )
        )
      })

    // OAuth callback login path.
    // Includes provider-specific callback URL and optional post-login redirect.
    const loginWithOAuthCode: (
      provider: OAuthProvider,
      code: string,
      state: string
    ) => Effect.Effect<LoginWithOAuthCodeResult> = Effect.fn('Auth.loginWithOAuthCode')((
      provider: OAuthProvider,
      code: string,
      state: string
    ): Effect.Effect<LoginWithOAuthCodeResult> => {
      return enqueueAuthCommand(
        runHandledAuthCommand<OAuthLoginResponse>(
          {
            operation: 'loginWithOAuthCode',
            unknownErrorMessage: 'Failed to log in'
          },
          sdk.send({
            type: 'accounts/loginWithOAuthCode',
            provider,
            code: code,
            oauthCallbackUrl: `${oauthRedirectBaseUrl}/oauth/callback/${provider}`,
            state
          })
        ).pipe(
          Effect.matchEffect({
            onFailure: (error) =>
              Effect.succeed({ status: 'failed', error } satisfies LoginWithOAuthCodeResult),
            onSuccess: (res): Effect.Effect<LoginWithOAuthCodeResult> => {
              if (res.error) {
                return Effect.succeed({ status: 'failed', error: res.error })
              }

              return loggedInEffect(res.user).pipe(
                Effect.as({
                  status: 'succeeded',
                  user: res.user,
                  redirectUrl: res.postLoginRedirectUrl
                })
              )
            }
          })
        )
      )
    })

    // Reset-password command with local password confirmation validation.
    // Returns AuthError on validation/command failures.
    const resetPasswordOrFail: (
      token: string,
      password: string,
      passwordConfirmation: string
    ) => Effect.Effect<User | undefined, AuthError> = Effect.fn('Auth.resetPassword')((
      token: string,
      password: string,
      passwordConfirmation: string
    ): Effect.Effect<User | undefined, AuthError> => {
      const mismatch: AuthError = {
        type: 'password_confirmation_mismatch',
        message: 'Passwords do not match'
      }
      if (password != passwordConfirmation) {
        return Effect.fail(mismatch)
      }

      return enqueueAuthCommand(
        authSendRecoverable(
          sdk.send({
            type: 'accounts/resetPassword',
            resetPasswordToken: token,
            password,
            passwordConfirmation
          })
        ).pipe(
          Effect.tapError((error) => reportAuthHandledError('resetPassword', error)),
          Effect.mapError(() => toAuthFailure('Failed to reset password')),
          Effect.flatMap(raiseAuthErrorEffect),
          Effect.tap((res) => {
            if (!res.user) {
              return Effect.void
            }
            return loggedInEffect(res.user)
          }),
          Effect.map((res) => res.user)
        )
      )
    })

    // Public reset-password API used by callers that prefer Result-style return shape.
    const resetPassword = (
      token: string,
      password: string,
      passwordConfirmation: string
    ): Effect.Effect<
      { ok: true; value: { user: User | undefined } } | { ok: false; error: AuthError },
      never
    > =>
      resetPasswordOrFail(token, password, passwordConfirmation).pipe(
        Effect.match({
          onFailure: (error) => ({ ok: false as const, error }),
          onSuccess: (user) => ({ ok: true as const, value: { user } })
        })
      )

    // Logout is best-effort:
    // even if logout command errors, we still report and transition local auth state to logged out.
    const logout: () => Effect.Effect<void> = Effect.fn('Auth.logout')((): Effect.Effect<void> => {
      const reportLogoutFailure = (error: AuthTransportError) =>
        reportAuthHandledError('logout', error).pipe(Effect.as(undefined))

      return enqueueAuthCommand(
        authSendRecoverable(sdk.send({ type: 'accounts/logout' })).pipe(
          Effect.catchTags({
            TransportError: reportLogoutFailure,
            TimeoutError: reportLogoutFailure,
            HttpStatusError: reportLogoutFailure
          }),
          Effect.andThen(loggedOutEffect())
        )
      )
    })

    // Sends a password-reset email. Queued for ordering with other auth operations.
    const sendPasswordResetEmail = (email: string, continueUrl: string) => {
      return enqueueAuthCommand(
        runHandledAuthCommand(
          {
            operation: 'sendPasswordResetEmail',
            unknownErrorMessage: 'Failed to send password reset email'
          },
          sdk.send({
            type: 'accounts/sendPasswordResetEmail',
            email,
            continueUrl
          })
        )
      )
    }

    // Updates the user's display name. Queued for ordering with other auth operations.
    const setProfileName = (userId: string, profileName: string) => {
      return enqueueAuthCommand(
        runHandledAuthCommand(
          {
            operation: 'setProfileName',
            unknownErrorMessage: 'Failed to update profile name'
          },
          sdk.send({
            type: 'accounts/setProfileName',
            userId,
            profileName
          })
        ).pipe(
          Effect.andThen(
            updateLoggedInUserEffect((user) => {
              if (user.id != userId) {
                return user
              }
              return { ...user, name: profileName }
            })
          ),
          Effect.asVoid
        )
      )
    }

    // Triggers a phone verification SMS. Queued for ordering with other auth operations.
    const sendPhoneNumberVerificationCode = () => {
      return enqueueAuthCommand(
        runHandledAuthCommand(
          {
            operation: 'sendPhoneNumberVerificationCode',
            unknownErrorMessage: 'Failed to send verification code'
          },
          sdk.send({ type: 'accounts/sendPhoneNumberVerificationCode' })
        ).pipe(Effect.asVoid)
      )
    }

    // Changes the user's phone number (requires password + optional code). Queued for ordering with other auth operations.
    const updatePhoneNumber = (args: {
      phoneNumber: string
      currentPassword: string
      code?: string
    }) => {
      return enqueueAuthCommand(
        runHandledAuthCommand(
          {
            operation: 'updatePhoneNumber',
            unknownErrorMessage: 'Failed to update phone number'
          },
          sdk.send({
            type: 'accounts/updatePhoneNumber',
            phoneNumber: args.phoneNumber,
            currentPassword: args.currentPassword,
            code: args.code
          })
        ).pipe(
          Effect.tap((response) => {
            if (response.status != 'success') {
              return Effect.void
            }

            return updateLoggedInUserEffect((user) => {
              if (args.phoneNumber) {
                return { ...user, phoneNumber: args.phoneNumber }
              }

              const { phoneNumber: _phoneNumber, mfaMethod: _mfaMethod, ...userWithoutPhone } = user
              return userWithoutPhone
            })
          })
        )
      )
    }

    // Confirms the user's phone number with a verification code. Queued for ordering with other auth operations.
    const verifyPhoneNumber = (code: string) => {
      return enqueueAuthCommand(
        runHandledAuthCommand(
          {
            operation: 'verifyPhoneNumber',
            unknownErrorMessage: 'Failed to verify phone number'
          },
          sdk.send({ type: 'accounts/verifyPhoneNumber', code })
        )
      )
    }

    // Enables MFA for the specified method. Queued for ordering with other auth operations.
    const enableMultiFactorAuthentication = (method: 'phone_number') => {
      return enqueueAuthCommand(
        runHandledAuthCommand(
          {
            operation: 'enableMultiFactorAuthentication',
            unknownErrorMessage: 'Failed to enable multi factor authentication'
          },
          sdk.send({ type: 'accounts/enableMultiFactorAuthentication', method })
        ).pipe(
          Effect.andThen(updateLoggedInUserEffect((user) => ({ ...user, mfaMethod: method }))),
          Effect.asVoid
        )
      )
    }

    // Teardown hook called when the app store / runtime is disposed.
    //
    // Stops both owned fibers before clearing the queue and cache. This
    // ordering lets in-flight physical requests run their interruption finalizers.
    const shutdown = (): Effect.Effect<void, never, never> =>
      Fiber.interrupt(startupRefresh).pipe(
        Effect.andThen(Fiber.interrupt(authCommandWorker)),
        Effect.andThen(Queue.shutdown(authCommandQueue)),
        Effect.andThen(Cache.invalidateAll(csrfTokenCache)),
        Effect.as(undefined)
      )

    return {
      logout,
      statuses,
      signUp,
      loginWithEmailPassword,
      completeLoginWithMFACode,
      loginWithMagicLink,
      loginWithOAuthCode,
      resetPassword,
      sendPasswordResetEmail,
      setProfileName,
      sendPhoneNumberVerificationCode,
      updatePhoneNumber,
      verifyPhoneNumber,
      enableMultiFactorAuthentication,
      shutdown,
      getHeaders,
      sessionRejected: requestSessionReconciliation,
      csrfRejected: clearCsrfToken
    } satisfies LiveAuthClient
  })
}

// Converts low-level HttpClient errors into the same taxonomy as SDK commands.
function toHttpClientTransportError(
  error: HttpClientError.HttpClientError,
  request: { url: string; method: string; headers?: Record<string, string> }
) {
  const classification = inferFetchFailure({
    request: { url: request.url, method: request.method },
    error: error.cause ?? error
  })

  const requestTrace = { url: request.url, method: request.method, headers: request.headers }

  if (classification.type == 'timeout') {
    return new TimeoutError(requestTrace, {
      cause: error,
      description: error.reason.description,
      timeoutMs: classification.timeoutMs
    })
  }

  if (classification.type == 'invalidRequest') {
    return new InvalidRequestError(requestTrace, 'invalid_url', {
      cause: error,
      description: error.reason.description
    })
  }

  return new TransportError(requestTrace, {
    cause: error,
    description: error.reason.description,
    classification
  })
}

// Type guard for the specific status that should trigger CSRF invalidation/retry.
function isInvalidCsrfError(error: unknown): error is HttpStatusError {
  return (
    isHttpStatusError(error) &&
    error.response.status == 403 &&
    error.response.body != null &&
    typeof error.response.body == 'object' &&
    (error.response.body as Record<string, unknown>).error == 'invalid_csrf'
  )
}

function isTransientHttpStatus(status: number) {
  return status == 408 || status == 429 || status >= 500
}

// Standard helper for command responses that encode domain errors in payload.
function raiseAuthErrorEffect<T extends { error?: AuthError }>(res: T) {
  if (res.error) {
    return Effect.fail(toAuthFailure(res.error))
  }

  return Effect.succeed(res)
}

// Fallback AuthError constructor used when transport/internal details should not leak.
function toUnknownAuthError(message: string): AuthError {
  return {
    type: 'unknown',
    message
  }
}

function toAuthFailure(error: AuthError): AuthFailure
function toAuthFailure(message: string): AuthFailure
function toAuthFailure(errorOrMessage: AuthError | string): AuthFailure {
  if (typeof errorOrMessage == 'string') {
    return { ...toUnknownAuthError(errorOrMessage), _tag: 'AuthFailure' }
  }
  return { ...errorOrMessage, _tag: 'AuthFailure' }
}
